import { Link } from '@tanstack/react-router'
import { useMidiStore, VIRTUAL_SEQUENCER_INPUT_ID } from '@/stores/midiStore'

/**
 * "Routed to: X, Y" chip. Reads the MIDI store to figure out which
 * hardware outputs the sequencer's virtual input is currently
 * cabled to, and offers a shortcut to /midi when nothing is routed
 * yet so the user knows where to go.
 */
export function VirtualInputBadge() {
  const routes = useMidiStore(s => s.routes)
  const hwOutputs = useMidiStore(s => s.hwOutputs)

  const seqRoutes = routes.filter(r => r.inputId === VIRTUAL_SEQUENCER_INPUT_ID)
  const outputNames = seqRoutes
    .map(r => hwOutputs.find(o => o.id === r.outputId)?.name)
    .filter((name): name is string => Boolean(name))

  if (outputNames.length === 0) {
    return (
      <Link
        to="/werkshop/midi"
        className="flex items-center gap-1.5 sm:gap-2 rounded-lg border border-dashed border-brd-emphasis px-2 sm:px-3 py-1 sm:py-1.5 text-[11px] font-mono text-content-tertiary hover:border-second hover:text-second transition-colors"
        title="Wire Sequencer → a hardware output in the MIDI Router"
      >
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-content-tertiary" />
        <span className="sm:hidden">No MIDI route</span>
        <span className="hidden sm:inline">
          MIDI not routed · open router →
        </span>
      </Link>
    )
  }

  return (
    <Link
      to="/werkshop/midi"
      className="flex items-center gap-1.5 sm:gap-2 rounded-lg border border-second bg-[color:var(--second-soft)] px-2 sm:px-3 py-1 sm:py-1.5 text-[11px] font-mono text-second hover:brightness-110 transition-[filter]"
      title={`Routed to ${outputNames.join(', ')}`}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-second" />
      <span className="sm:hidden">MIDI → {outputNames.length}</span>
      <span className="hidden sm:inline">
        Routed to: {outputNames.join(', ')}
      </span>
    </Link>
  )
}
